import type { Lead } from '../../../shared/types';
import { EMAIL_PATTERN, ISO_DATE_PATTERN } from '../../../shared/validation';

export type LeadFormField = 'email' | 'place_id' | 'contract_end_date';

export interface LeadFormError {
  field: LeadFormField;
  message: string;
}

/**
 * Checks the lead fields the API can reject beyond what the inputs already bound (maxlength,
 * number clamping). Run before saving: a rejected op is parked as "failed" and the lead (or the
 * edit) disappears from the phone after the next sync. Pass the full draft for a create and only
 * the changed fields for an edit (what the PATCH will carry); `selfId` is the lead being edited.
 */
export const leadFormErrors = (
  draft: Partial<Pick<Lead, 'email' | 'place_id' | 'contract_end_date'>>,
  others: readonly Pick<Lead, 'id' | 'business_name' | 'place_id'>[],
  selfId?: string,
): LeadFormError[] => {
  const errors: LeadFormError[] = [];
  if (draft.email && !EMAIL_PATTERN.test(draft.email)) {
    errors.push({ field: 'email', message: 'Enter a full email address, like owner@business.com' });
  }
  if (draft.contract_end_date && !ISO_DATE_PATTERN.test(draft.contract_end_date)) {
    errors.push({ field: 'contract_end_date', message: 'Contract end date needs a 4-digit year' });
  }
  if (draft.place_id) {
    const owner = others.find((l) => l.id !== selfId && l.place_id === draft.place_id);
    if (owner) {
      errors.push({
        field: 'place_id',
        message: `This Google place is already linked to ${owner.business_name}`,
      });
    }
  }
  return errors;
};
